import { i } from "@instantdb/admin";
import type { Category } from "./src/category.ts";

const _schema = i.schema({
  entities: {
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.string(),
    }),
    $users: i.entity({
      email: i.string().unique().indexed().optional(),
      phone: i.string().indexed().optional(),
      phoneVerified: i.boolean().indexed().optional(),
    }),
    ticketTrades: i.entity({
      isBuy: i.boolean().indexed(),
      time: i.number().indexed(),
      phone: i.string().indexed(),
      eventId: i.string().indexed(),
      note: i.string().optional(),
      source: i.string().optional(),
      isOneOnOne: i.boolean().optional(),
      status: i.string().optional(),
    }),
    ticketTradeEvidences: i.entity({
      messageId: i.string().unique().indexed(),
      ticketTradeId: i.string().indexed(),
    }),
    subscriptions: i.entity({
      blacklistedWords: i.json<string[]>().optional(),
      whitelistedWords: i.json<string[]>().optional(),
      categories: i.json<Category[]>(),
      blacklistedCategories: i.json<Category[]>().optional(),
      country: i.string().indexed(),
      radiusKm: i.number().optional(),
      location: i.string().optional(),
      phone: i.string(),
      // When set, this subscription belongs to a group chat rather than an individual
      conversationId: i.string().optional(),
      // RFC 5545 RRULE for push aggregation schedule, e.g. "FREQ=DAILY" or "FREQ=WEEKLY;BYDAY=SU,TH"
      pushRRule: i.string().optional(),
      // Local hour (0-23) in user's timezone when to send the aggregated push
      pushLocalHour: i.number().optional(),
      // Local minute (0-59) in user's timezone when to send the aggregated push
      pushLocalMinute: i.number().optional(),
      // Last time (ms since epoch) we sent an aggregated push for this subscription
      lastAggregatedPushAt: i.number().optional(),
      // Accumulated matching event IDs waiting for the next scheduled push
      waitingEventIds: i.json<string[]>().optional(),
      // "buy" | "sell" | "both" — when set, also notify about matching ticket trades
      ticketAlerts: i.string().optional(),
      // Accumulated matching ticket trade IDs waiting for the next scheduled push
      waitingTicketTradeIds: i.json<string[]>().optional(),
      // Max number of events to notify about in a single push
      maxEvents: i.number().optional(),
      // Minimum taste match score (0-100) to qualify for notification
      minTasteMatch: i.number().optional(),
      // The exact timestamp of the next scheduled push in QStash, used to ignore outdated tasks
      scheduledPushAt: i.number().optional(),
      // When true, matching events passing the taste threshold (minTasteMatch,
      // default 85) are pushed immediately on ingestion (rate-limited per
      // user). The subscription does not accumulate waiting events and its
      // scheduled push is computed fresh at push time.
      instant: i.boolean().optional().indexed(),
    }),
    promotions: i.entity({
      eventId: i.string().indexed().unique(),
      targetAudience: i.string().indexed(),
      expiration: i.number().indexed(),
      promotionLink: i.string().optional(),
      marketingBudget: i.number().optional(),
    }),
    promotionClicks: i.entity({
      phone: i.string().indexed(),
      timestamp: i.number().indexed(),
    }),
    promotionBalances: i.entity({
      balance: i.number(),
    }),
    users: i.entity({
      phone: i.string().unique().indexed(),
      name: i.string().optional(),
    }),
    payments: i.entity({
      stripeSessionId: i.string().unique().indexed(),
      amount: i.number(),
      credits: i.number(),
      timestamp: i.number().indexed(),
    }),
    stripeSubscriptions: i.entity({
      stripeSubscriptionId: i.string().unique().indexed(),
      stripeCustomerId: i.string().indexed().optional(),
      status: i.string().indexed(),
      priceId: i.string().indexed().optional(),
      currentPeriodEnd: i.number().indexed().optional(),
      cancelAtPeriodEnd: i.boolean().optional(),
      createdAt: i.number().indexed(),
    }),
    swipes: i.entity({
      eventId: i.string().indexed(),
      direction: i.string().indexed(),
      timestamp: i.number().indexed(),
      shareWithFriends: i.boolean().indexed().optional(),
      phone: i.string().indexed().optional(),
      originChatId: i.string().indexed().optional(),
      quoteText: i.string().optional(),
    }),
    friendRequests: i.entity({
      fromPhone: i.string().indexed(),
      toPhone: i.string().indexed(),
      fromName: i.string().optional(),
      status: i.string().indexed(),
      createdAt: i.number().indexed(),
    }),
    whatsappGroups: i.entity({
      whatsappId: i.string().unique().indexed(),
      name: i.string().optional(),
      inviteLink: i.string().optional().indexed(),
      joinedAt: i.number().optional().indexed(),
      status: i.string().optional().indexed(),
      memberCount: i.number().optional(),
      description: i.string().optional(),
      lastSeenAt: i.number().optional().indexed(),
    }),
    spyBots: i.entity({
      phone: i.string().unique().indexed(),
      token: i.string(),
      isPassive: i.boolean().indexed(),
    }),
    instagramSources: i.entity({
      username: i.string().unique().indexed(),
      url: i.string().optional(),
      categories: i.json<Category[]>().optional(),
      status: i.string().indexed(),
      addedAt: i.number().indexed(),
      addedBy: i.string(),
    }),
    linkScrapeSources: i.entity({
      source: i.string().indexed(),
      url: i.string().unique().indexed(),
      categories: i.json<Category[]>().optional(),
      selector: i.string().optional(),
      useProxy: i.boolean().optional(),
      status: i.string().indexed(),
      addedAt: i.number().indexed(),
      addedBy: i.string(),
      lastNoEventsAlertAt: i.number().optional().indexed(),
    }),
    sourceRequests: i.entity({
      kind: i.string().indexed(),
      status: i.string().indexed(),
      createdAt: i.number().indexed(),
      reviewedAt: i.number().optional().indexed(),
      reviewerEmail: i.string().optional(),
      requesterUserId: i.string().indexed(),
      requesterEmail: i.string().optional(),
      discoverySource: i.string().optional().indexed(),
      sourceUrl: i.string().indexed(),
      instagramUsername: i.string().optional().indexed(),
      title: i.string(),
      requestText: i.string(),
      memberCount: i.number().optional(),
      recommendation: i.string().optional(),
      payload: i.json<unknown>(),
      resolution: i.json<unknown>().optional(),
      conversationId: i.string().optional(),
      messageId: i.string().optional(),
    }),
    clicks: i.entity({
      userId: i.string().indexed(),
      eventId: i.string().indexed(),
      timestamp: i.number().indexed(),
      inorganic: i.boolean().indexed().optional(),
    }),
    searches: i.entity({
      userId: i.string().indexed(),
      categories: i.json<string[]>().optional(),
      performers: i.json<string[]>().optional(),
      venues: i.json<string[]>().optional(),
      productions: i.json<string[]>().optional(),
      location: i.string().optional().indexed(),
      resultCount: i.number().optional().indexed(),
      timestamp: i.number().indexed(),
    }),
    partners: i.entity({
      name: i.string().indexed(),
      secret: i.string().indexed(),
      createdAt: i.number().indexed(),
    }),
    apiTokens: i.entity({
      userId: i.string().indexed(),
      name: i.string().indexed(),
      description: i.string().optional(),
      secretHash: i.string(),
      unlimitedQuota: i.boolean().optional(),
      createdAt: i.number().indexed(),
      createdBy: i.string().optional(),
    }),
  },
  links: {
    userSwipes: {
      forward: { on: "$users", has: "many", label: "swipes" },
      reverse: { on: "swipes", has: "one", label: "user" },
    },
    userFriends: {
      forward: { on: "users", has: "many", label: "friends" },
      reverse: { on: "users", has: "many", label: "friendedBy" },
    },
    promotionClickPromotion: {
      forward: { on: "promotionClicks", has: "one", label: "promotion" },
      reverse: { on: "promotions", has: "many", label: "clicks" },
    },
    promotionBillingUser: {
      forward: { on: "promotions", has: "one", label: "billingUser" },
      reverse: { on: "users", has: "many", label: "billedPromotions" },
    },
    promotionBalanceUser: {
      forward: { on: "promotionBalances", has: "one", label: "user" },
      reverse: { on: "users", has: "one", label: "promotionBalance" },
    },
    paymentUser: {
      forward: { on: "payments", has: "one", label: "user" },
      reverse: { on: "users", has: "many", label: "payments" },
    },
    subscriptionUser: {
      forward: { on: "stripeSubscriptions", has: "one", label: "user" },
      reverse: { on: "users", has: "many", label: "stripeSubscriptions" },
    },
  },
  rooms: {},
});

type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
